import { SkinTypeDefStorage } from '../../storage/SkinTypeDefStorage';
import { Base64ImageStorage, extractImgSrc } from '../../storage/Base64ImageStorage';
import type { StorageInterface } from '../../stores/childNavStores';

/**
 * The subset of a SkinTypeDef's ChannelInfo settings that drive Pro Groups' own main
 * menu header bar. Each field is null when the skin doesn't define it (or the referenced
 * Base64Image record hasn't synced yet), so callers can fall back to their own defaults
 * rather than rendering an empty value.
 */
export interface SkinChannelInfo {
  logoSrc: string | null;
  backgroundSrc: string | null;
  menuFontColor: string | null;
  skinBgColor: string | null;
}

const EMPTY_CHANNEL_INFO: SkinChannelInfo = {
  logoSrc: null,
  backgroundSrc: null,
  menuFontColor: null,
  skinBgColor: null,
};

/** ChannelInfo entries are {name, value} pairs — match case-insensitively, same convention as PHI field lookups. */
function findChannelInfoValue(channelInfo: Array<{ name: string; value: string }>, key: string): string {
  const keyLower = key.toLowerCase();
  return channelInfo.find((entry) => entry.name?.toLowerCase() === keyLower)?.value ?? '';
}

/**
 * Resolves the bundled SkinTypeDef content record (synced locally via SkinTypeDefStorage,
 * same bundle-sync mechanism as assessment content) for the given SkinType — normally
 * PracInfoDto.SkinType for the active account — into the ChannelInfo settings that style
 * Pro Groups' main menu header bar. logoimageid/backgroundimageid are resolved through
 * Base64ImageStorage into displayable data-URIs, the same lookup already used for the
 * expand/collapse and PDF icons elsewhere in the app.
 */
export async function resolveSkinChannelInfo(
  storage: StorageInterface,
  skinType: string,
  languageTypeId = '1'
): Promise<SkinChannelInfo> {
  if (!skinType) {
    return EMPTY_CHANNEL_INFO;
  }

  const skinDef = await new SkinTypeDefStorage(storage).getSkinTypeDef(skinType, languageTypeId);
  if (!skinDef) {
    return EMPTY_CHANNEL_INFO;
  }

  const channelInfo = skinDef.channelInfo ?? [];
  const logoImageId = findChannelInfoValue(channelInfo, 'logoimageid');
  const backgroundImageId = findChannelInfoValue(channelInfo, 'backgroundimageid');

  const imageStorage = new Base64ImageStorage(storage);
  const [logoImage, backgroundImage] = await Promise.all([
    logoImageId ? imageStorage.getImage(logoImageId) : Promise.resolve(null),
    backgroundImageId ? imageStorage.getImage(backgroundImageId) : Promise.resolve(null),
  ]);

  return {
    logoSrc: (logoImage?.base64 && extractImgSrc(logoImage.base64)) || null,
    backgroundSrc: (backgroundImage?.base64 && extractImgSrc(backgroundImage.base64)) || null,
    menuFontColor: findChannelInfoValue(channelInfo, 'menufontcolor') || null,
    skinBgColor: findChannelInfoValue(channelInfo, 'skinbgcolor') || null,
  };
}
